import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart, Building, Stethoscope, CheckCircle, Shield, Mail, KeyRound, RefreshCw, Fingerprint } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4b79ec74-cc21-403d-a390-656b8c7298f7/artifacts/m95yszhz_1.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper functions for WebAuthn
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Professional types for external professionals
const PROFESSIONAL_TYPES = [
  { value: 'gp', label: 'GP (General Practitioner)' },
  { value: 'paediatrician', label: 'Paediatrician' },
  { value: 'neurologist', label: 'Neurologist' },
  { value: 'psychiatrist', label: 'Psychiatrist' },
  { value: 'psychologist', label: 'Psychologist' },
  { value: 'social_worker', label: 'Social Worker' },
  { value: 'educational_psychologist', label: 'Educational Psychologist' },
  { value: 'senco', label: 'SENCO' },
  { value: 'health_visitor', label: 'Health Visitor' },
  { value: 'school_nurse', label: 'School Nurse' },
  { value: 'dietitian', label: 'Dietitian' },
  { value: 'physiotherapist', label: 'Physiotherapist' },
  { value: 'occupational_therapist', label: 'Occupational Therapist' },
  { value: 'speech_therapist', label: 'Speech & Language Therapist' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'other', label: 'Other' }
];

const REGISTRATION_BODIES = [
  { value: 'GMC', label: 'GMC (General Medical Council)' },
  { value: 'HCPC', label: 'HCPC (Health & Care Professions Council)' },
  { value: 'NMC', label: 'NMC (Nursing & Midwifery Council)' },
  { value: 'SWE', label: 'SWE (Social Work England)' },
  { value: 'GPhC', label: 'GPhC (General Pharmaceutical Council)' },
  { value: 'BPS', label: 'BPS (British Psychological Society)' },
  { value: 'Other', label: 'Other' }
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register, verifyMfa, setAuthData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [professionalSubmitted, setProfessionalSubmitted] = useState(false);
  
  // MFA State
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // WebAuthn / MFA Options State
  const [mfaOptions, setMfaOptions] = useState([]);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState('email');
  const [webauthnLoading, setWebauthnLoading] = useState(false);
  const [webauthnAvailable, setWebauthnAvailable] = useState(false);
  
  // Check WebAuthn support
  useEffect(() => {
    const checkSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setWebauthnAvailable(available);
        } catch {
          setWebauthnAvailable(false);
        }
      }
    };
    checkSupport();
  }, []);
  
  // Check for session timeout on mount
  useState(() => {
    const timeout = sessionStorage.getItem('session_timeout');
    if (timeout === 'true') {
      setSessionExpired(true);
      sessionStorage.removeItem('session_timeout');
    }
  }, []);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'therapist',
    discipline: '',
    phone: ''
  });
  
  // Professional self-registration form
  const [professionalData, setProfessionalData] = useState({
    professional_type: '',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organisation: '',
    role_title: '',
    registration_number: '',
    registration_body: '',
    relationship_statement: '',
    specialties: []
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, loginData);
      const data = response.data;
      
      // Check if MFA is required
      if (data.mfa_required) {
        setMfaUserId(data.user_id);
        setMfaEmail(data.message); // Contains masked email
        
        // Build MFA options based on response
        const options = [{ type: 'email', name: 'Email Code', description: 'Enter the 6-digit code sent to your email' }];
        
        // Add biometric option if available for this user
        if (data.webauthn_available && webauthnAvailable) {
          options.push({ type: 'webauthn', name: 'Biometric / Passkey', description: 'Use fingerprint, face ID, or security key' });
        }
        
        setMfaOptions(options);
        setSelectedMfaMethod('email'); // Default to email
        
        setShowMfaDialog(true);
        toast.info('Please verify your identity to continue');
        return;
      }
      
      // No MFA - proceed with normal login
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle WebAuthn Authentication
  const handleWebauthnAuth = async () => {
    if (!webauthnAvailable) {
      toast.error('Biometric authentication not available on this device');
      return;
    }
    
    setWebauthnLoading(true);
    try {
      // Start WebAuthn authentication
      const startRes = await axios.post(`${API_URL}/api/webauthn/authenticate/start`, {
        email: loginData.email
      });
      
      const options = startRes.data.options;
      const challengeId = startRes.data.challenge_id;
      
      // Convert options for navigator.credentials.get
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout,
        rpId: options.rpId,
        userVerification: options.userVerification,
        allowCredentials: (options.allowCredentials || []).map(cred => ({
          type: cred.type,
          id: base64urlToBuffer(cred.id),
          transports: cred.transports
        }))
      };
      
      // Get credential
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });
      
      // Convert credential for server
      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
        }
      };
      
      // Complete authentication
      const completeRes = await axios.post(`${API_URL}/api/webauthn/authenticate/complete`, {
        challenge_id: challengeId,
        credential: credentialJSON
      });
      
      // Success - set auth data and navigate
      const { access_token, user } = completeRes.data;
      localStorage.setItem('icrcl_token', access_token);
      localStorage.setItem('icrcl_user', JSON.stringify(user));
      
      // Use setAuthData if available, otherwise reload
      if (setAuthData) {
        setAuthData(access_token, user);
      }
      
      setShowMfaDialog(false);
      toast.success('Welcome back!');
      navigate('/');
      
    } catch (err) {
      console.error('WebAuthn error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Authentication was cancelled or timed out');
      } else if (err.name === 'SecurityError') {
        toast.error('Security error - please try again');
      } else {
        toast.error(err.response?.data?.detail || 'Biometric authentication failed');
      }
    } finally {
      setWebauthnLoading(false);
    }
  };

  // Handle MFA verification
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setMfaLoading(true);
    try {
      await verifyMfa(mfaUserId, mfaCode);
      setShowMfaDialog(false);
      toast.success('Verification successful! Welcome back.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setMfaCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  // Resend MFA code
  const handleResendMfaCode = async () => {
    setResendingCode(true);
    try {
      // Re-attempt login to resend the code
      await axios.post(`${API_URL}/api/auth/login`, loginData);
      toast.success('A new verification code has been sent to your email');
    } catch (error) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setResendingCode(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail;
      
      if (status === 202) {
        // Registration pending approval - this is a success case
        toast.success(message || 'Registration submitted! Your account is pending approval.');
        // Reset register form
        setRegisterData({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role: 'therapist',
          discipline: '',
          phone: ''
        });
        // Pre-fill email in login form for convenience
        setLoginData({ email: registerData.email, password: '' });
      } else {
        toast.error(message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Professional Self-Registration
  const handleProfessionalRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/professionals/self-register`, professionalData);
      setProfessionalSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1e3a5f] p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-[#2a9d8f]"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-[#4ade80]"></div>
          <div className="absolute bottom-20 left-40 w-32 h-32 rounded-full bg-[#f472b6]"></div>
        </div>
        
        <div className="relative z-10">
          <img src={LOGO_URL} alt="ICRCL Logo" className="w-32 h-32 object-contain" />
        </div>
        
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Innovative Care and<br />Rehab Centre Limited
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Comprehensive healthcare management platform for children's therapy services. 
            Supporting OT, PT, SLT and more.
          </p>
          <div className="flex items-center gap-2 text-white/60">
            <Heart className="w-4 h-4 text-[#f472b6]" />
            <span className="text-sm">Caring for children across the UK</span>
          </div>
        </div>
        
        <div className="relative z-10 text-white/40 text-sm">
          <p>CQC/Ofsted Ready • UK GDPR Compliant • NHS Integrated</p>
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="ICRCL Logo" className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1e3a5f]">ICRCL</h1>
            <p className="text-slate-500 text-sm">Healthcare Platform</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="register">Staff Register</TabsTrigger>
              <TabsTrigger value="professional">
                <Stethoscope className="w-3 h-3 mr-1" />
                Professional
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-0 shadow-xl">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl text-[#1e3a5f]">Welcome back</CardTitle>
                  <CardDescription>Sign in to your ICRCL account</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Session Timeout Alert */}
                  {sessionExpired && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                      <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Session Expired</p>
                        <p className="text-amber-600">
                          Your session was automatically logged out due to inactivity. Please sign in again.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Forgot Password Link */}
                    <div className="text-right">
                      <a 
                        href="/reset-password" 
                        className="text-sm text-[#1e3a5f] hover:underline"
                      >
                        Forgot your password?
                      </a>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                    
                    {/* Demo Credentials Info */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-800 font-medium mb-1">Demo Access:</p>
                      <p className="text-xs text-blue-600">
                        Default password for all new accounts: <code className="bg-blue-100 px-1 rounded font-mono">password123</code>
                      </p>
                      <p className="text-xs text-blue-500 mt-1">
                        Please change your password after first login.
                      </p>
                    </div>

                    {/* Parent Registration Link */}
                    <div className="mt-4 text-center pt-4 border-t">
                      <p className="text-sm text-slate-600">
                        Are you a parent/guardian?{' '}
                        <a 
                          href="/parent-register" 
                          className="text-[#2a9d8f] hover:underline font-medium"
                        >
                          Register here
                        </a>
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-0 shadow-xl">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl text-[#1e3a5f]">Create account</CardTitle>
                  <CardDescription>Join the ICRCL platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name</Label>
                        <Input
                          id="first_name"
                          placeholder="John"
                          value={registerData.first_name}
                          onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                          required
                          data-testid="register-firstname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name</Label>
                        <Input
                          id="last_name"
                          placeholder="Doe"
                          value={registerData.last_name}
                          onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                          required
                          data-testid="register-lastname"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="your@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <Select
                          value={registerData.role}
                          onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                        >
                          <SelectTrigger data-testid="register-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="director">Director</SelectItem>
                            <SelectItem value="managing_director">Managing Director</SelectItem>
                            <SelectItem value="clinical_lead">Clinical Lead</SelectItem>
                            <SelectItem value="educational_lead">Educational Lead</SelectItem>
                            <SelectItem value="therapist">Therapist</SelectItem>
                            <SelectItem value="rehab_assistant">Rehab Assistant</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="reception">Reception</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                            <SelectItem value="hr">HR Manager</SelectItem>
                            <SelectItem value="business_development">Business Development Manager</SelectItem>
                            <SelectItem value="support">Support Worker</SelectItem>
                            <SelectItem value="external_professional">External Professional</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-amber-600">All registrations require admin approval</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Discipline/Department</Label>
                        <Select
                          value={registerData.discipline}
                          onValueChange={(value) => setRegisterData({ ...registerData, discipline: value })}
                        >
                          <SelectTrigger data-testid="register-discipline">
                            <SelectValue placeholder="Select your discipline..." />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Clinical Disciplines */}
                            <SelectItem value="occupational_therapy">Occupational Therapy (OT)</SelectItem>
                            <SelectItem value="physiotherapy">Physiotherapy (PT)</SelectItem>
                            <SelectItem value="speech_language_therapy">Speech & Language Therapy (SLT)</SelectItem>
                            <SelectItem value="psychology">Psychology</SelectItem>
                            <SelectItem value="clinical_psychology">Clinical Psychology</SelectItem>
                            <SelectItem value="educational_psychology">Educational Psychology</SelectItem>
                            <SelectItem value="counselling">Counselling</SelectItem>
                            <SelectItem value="play_therapy">Play Therapy</SelectItem>
                            <SelectItem value="music_therapy">Music Therapy</SelectItem>
                            <SelectItem value="art_therapy">Art Therapy</SelectItem>
                            <SelectItem value="behavioural_therapy">Behavioural Therapy (ABA/PBS)</SelectItem>
                            <SelectItem value="nursing">Nursing</SelectItem>
                            <SelectItem value="dietetics">Dietetics & Nutrition</SelectItem>
                            <SelectItem value="podiatry">Podiatry</SelectItem>
                            <SelectItem value="orthoptics">Orthoptics</SelectItem>
                            {/* Education */}
                            <SelectItem value="special_education">Special Education (SEND)</SelectItem>
                            <SelectItem value="early_years">Early Years Education</SelectItem>
                            <SelectItem value="teaching">Teaching</SelectItem>
                            <SelectItem value="teaching_assistant">Teaching Assistant</SelectItem>
                            {/* Support & Rehab */}
                            <SelectItem value="rehab_support">Rehabilitation Support</SelectItem>
                            <SelectItem value="healthcare_assistant">Healthcare Assistant</SelectItem>
                            <SelectItem value="support_worker">Support Worker</SelectItem>
                            <SelectItem value="care_coordinator">Care Coordinator</SelectItem>
                            {/* Management & Admin */}
                            <SelectItem value="management">Management</SelectItem>
                            <SelectItem value="administration">Administration</SelectItem>
                            <SelectItem value="finance">Finance & Accounts</SelectItem>
                            <SelectItem value="hr">Human Resources</SelectItem>
                            <SelectItem value="reception">Reception</SelectItem>
                            <SelectItem value="it">IT & Systems</SelectItem>
                            <SelectItem value="quality_compliance">Quality & Compliance</SelectItem>
                            <SelectItem value="marketing">Marketing & Business Development</SelectItem>
                            {/* External */}
                            <SelectItem value="social_work">Social Work</SelectItem>
                            <SelectItem value="external_professional">External Professional</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        placeholder="+44 7123 456789"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        data-testid="register-phone"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* External Professional Registration Tab */}
            <TabsContent value="professional">
              <Card className="border-0 shadow-xl">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl text-[#1e3a5f] flex items-center gap-2">
                    <Building className="w-6 h-6" />
                    Professional Registration
                  </CardTitle>
                  <CardDescription>
                    Register as an external professional (GP, Consultant, Social Worker, etc.) to collaborate with ICRCL
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {professionalSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">Registration Submitted!</h3>
                      <p className="text-slate-600 mb-4">
                        Thank you for registering. Your application is now pending review by ICRCL staff.
                        You will be contacted once your registration has been approved.
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => setProfessionalSubmitted(false)}
                        className="mt-4"
                      >
                        Submit Another Registration
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleProfessionalRegister} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Professional Type *</Label>
                          <Select
                            value={professionalData.professional_type}
                            onValueChange={(value) => setProfessionalData({ ...professionalData, professional_type: value })}
                          >
                            <SelectTrigger data-testid="prof-type">
                              <SelectValue placeholder="Select type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {PROFESSIONAL_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Select
                            value={professionalData.title}
                            onValueChange={(value) => setProfessionalData({ ...professionalData, title: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Dr">Dr</SelectItem>
                              <SelectItem value="Mr">Mr</SelectItem>
                              <SelectItem value="Mrs">Mrs</SelectItem>
                              <SelectItem value="Ms">Ms</SelectItem>
                              <SelectItem value="Miss">Miss</SelectItem>
                              <SelectItem value="Prof">Prof</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="prof-first-name">First Name *</Label>
                          <Input
                            id="prof-first-name"
                            placeholder="John"
                            value={professionalData.first_name}
                            onChange={(e) => setProfessionalData({ ...professionalData, first_name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="prof-last-name">Last Name *</Label>
                          <Input
                            id="prof-last-name"
                            placeholder="Smith"
                            value={professionalData.last_name}
                            onChange={(e) => setProfessionalData({ ...professionalData, last_name: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="prof-email">Email *</Label>
                          <Input
                            id="prof-email"
                            type="email"
                            placeholder="your@email.com"
                            value={professionalData.email}
                            onChange={(e) => setProfessionalData({ ...professionalData, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="prof-phone">Phone</Label>
                          <Input
                            id="prof-phone"
                            placeholder="+44 7123 456789"
                            value={professionalData.phone}
                            onChange={(e) => setProfessionalData({ ...professionalData, phone: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="prof-org">Organisation / Practice</Label>
                          <Input
                            id="prof-org"
                            placeholder="e.g., NHS Trust, Private Clinic"
                            value={professionalData.organisation}
                            onChange={(e) => setProfessionalData({ ...professionalData, organisation: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="prof-role">Job Title</Label>
                          <Input
                            id="prof-role"
                            placeholder="e.g., Consultant Paediatrician"
                            value={professionalData.role_title}
                            onChange={(e) => setProfessionalData({ ...professionalData, role_title: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="prof-reg-num">Registration Number</Label>
                          <Input
                            id="prof-reg-num"
                            placeholder="e.g., GMC number"
                            value={professionalData.registration_number}
                            onChange={(e) => setProfessionalData({ ...professionalData, registration_number: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Registration Body</Label>
                          <Select
                            value={professionalData.registration_body}
                            onValueChange={(value) => setProfessionalData({ ...professionalData, registration_body: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {REGISTRATION_BODIES.map((body) => (
                                <SelectItem key={body.value} value={body.value}>{body.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="prof-relationship">Relationship to ICRCL *</Label>
                        <Textarea
                          id="prof-relationship"
                          placeholder="Please briefly describe your relationship to ICRCL (e.g., 'I am the GP for several children you support', 'I am a referring consultant')"
                          value={professionalData.relationship_statement}
                          onChange={(e) => setProfessionalData({ ...professionalData, relationship_statement: e.target.value })}
                          rows={3}
                          required
                        />
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <strong>Note:</strong> Your registration will be reviewed by ICRCL staff. 
                        Once approved, you may be granted access to view information about children linked to your care.
                      </div>
                      
                      <Button
                        type="submit"
                        className="w-full bg-[#2a9d8f] hover:bg-[#2a9d8f]/90 rounded-full"
                        disabled={loading || !professionalData.professional_type || !professionalData.first_name || !professionalData.last_name || !professionalData.email || !professionalData.relationship_statement}
                        data-testid="prof-register-submit-btn"
                      >
                        {loading ? 'Submitting...' : 'Submit Registration'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <p className="text-center text-slate-400 text-xs mt-6">
            By continuing, you agree to ICRCL's Terms of Service and Privacy Policy
          </p>
        </div>
      </div>

      {/* MFA Verification Dialog */}
      <Dialog open={showMfaDialog} onOpenChange={setShowMfaDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Shield className="w-5 h-5" />
              Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              {mfaEmail}
            </DialogDescription>
          </DialogHeader>
          
          {/* MFA Method Selection */}
          {mfaOptions.length > 1 && (
            <div className="flex gap-2 mt-2">
              {mfaOptions.map((option) => (
                <Button
                  key={option.type}
                  type="button"
                  variant={selectedMfaMethod === option.type ? "default" : "outline"}
                  className={`flex-1 ${selectedMfaMethod === option.type ? 'bg-[#1e3a5f]' : ''}`}
                  onClick={() => setSelectedMfaMethod(option.type)}
                >
                  {option.type === 'email' ? (
                    <Mail className="w-4 h-4 mr-2" />
                  ) : (
                    <Fingerprint className="w-4 h-4 mr-2" />
                  )}
                  {option.name}
                </Button>
              ))}
            </div>
          )}
          
          {/* Email Code Form */}
          {selectedMfaMethod === 'email' && (
            <form onSubmit={handleMfaVerify} className="space-y-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Check your email</p>
                  <p className="text-blue-600 mt-1">
                    We've sent a 6-digit verification code to your registered email address. 
                    The code will expire in 10 minutes.
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mfa-code" className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Enter Verification Code
                </Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                  data-testid="mfa-code-input"
                />
                <p className="text-xs text-slate-500 text-center">
                  Enter the 6-digit code from your email
                </p>
              </div>
              
              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  className="w-full bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                  disabled={mfaLoading || mfaCode.length !== 6}
                  data-testid="mfa-verify-btn"
                >
                  {mfaLoading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendMfaCode}
                  disabled={resendingCode}
                  className="w-full"
                  data-testid="mfa-resend-btn"
                >
                  {resendingCode ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Code
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
          
          {/* WebAuthn / Biometric Form */}
          {selectedMfaMethod === 'webauthn' && (
            <div className="space-y-4 mt-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
                <Fingerprint className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium">Biometric Authentication</p>
                  <p className="text-purple-600 mt-1">
                    Use your fingerprint, face recognition, or device PIN to verify your identity.
                  </p>
                </div>
              </div>
              
              <Button
                type="button"
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={handleWebauthnAuth}
                disabled={webauthnLoading}
                data-testid="webauthn-verify-btn"
              >
                {webauthnLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-4 h-4 mr-2" />
                    Authenticate with Biometrics
                  </>
                )}
              </Button>
              
              {!webauthnAvailable && (
                <p className="text-xs text-amber-600 text-center">
                  Biometric authentication may not be available on this device
                </p>
              )}
            </div>
          )}
          
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => {
                setShowMfaDialog(false);
                setMfaCode('');
                setMfaUserId(null);
                setMfaOptions([]);
              }}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              Back to login
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
